<script setup>
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useClients } from "../../Composables/useClients.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_ClientList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: clients,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
    showArchived,
    getArchived,
    hideArchived,
} = useDataManager({
    baseUrl: '/api/clients',
    baseSearchUrl: '/api/search/clients',
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        last_name: 'asc',
        client_status: 'asc',
        created_at: 'desc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1' },
    { label: 'Nom', field: 'last_name', sortable: true, slot: 'name', class: 'col-2' },
    { label: 'Type', field: 'client_status', sortable: true, slot: 'client_status', class: 'col-1' },
    { label: 'Email', field: 'email', class: 'col-2' },
    { label: 'Téléphone', field: 'phone_number', class: 'col-1' },
    { label: 'Centres', slot: 'centers', class: 'col-2' },
    { label: 'Date création', field: 'created_at', sortable: true, slot: 'created_at', class: 'col-1' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        showArchived.value = savedSettings.showArchived || false
    }

    loadData(url.value)
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, showArchived],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newShowArchived]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            showArchived: newShowArchived,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToArchive = ref(null)

function showModal(modalId, id) {
    idToArchive.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    archiveClient,
    restoreClient,
} = useClients({
    onArchived: () => {
        loadData(url.value)
        hideModal('modalArchive')
    },
    onRestored: () => {
        loadData(url.value)
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchClients">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-md-auto ms-auto">
                            <button type="button" class="btn btn-dark waves-light me-2" @click="getArchived" v-if="!showArchived">
                                <i class="ri-archive-fill me-1 align-bottom"></i> Archives
                            </button>
                            <button type="button" class="btn btn-success waves-light me-2" @click="hideArchived" v-else>
                                <i class="ri-group-fill me-1 align-bottom"></i> Clients
                            </button>
                            <router-link :to="{ name:'clients.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_client">
                                <i class="ri-add-fill me-1 align-bottom"></i> Ajouter
                            </router-link>
                        </div>
                    </div>
                </div>
                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="clients"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Nom -->
                        <template #name="{ item }">
                            {{ item.data.last_name }} {{ item.data.first_name }}
                        </template>

                        <!-- Type (client_status) -->
                        <template #client_status="{ item }">
                            <span :class="'badge fs-11 bg-'+item.data.client_status_badge_color+'-subtle text-'+item.data.client_status_badge_color">
                                {{ item.data.client_status_label_fr }}
                            </span>
                        </template>

                        <!-- Centres -->
                        <template #centers="{ item }">
                            <div style="display: flex; align-items: center;" :title="item.data.centers?.map(center => center.data.name).join('\n')">
                                <i :class="item.data.centers?.length === 1
                                    ? 'ri-map-pin-line text-success me-2 fs-15'
                                    : 'ri-earth-line text-secondary me-2 fs-15'">
                                </i>
                                {{ item.data.centers?.length === 1 ? item.data.centers[0].data.name : 'Multi-centres' }}
                            </div>
                        </template>

                        <!-- Date création -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at_fr }}
                        </template>

                        <!-- Détails -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'clients.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">
                                Accéder
                            </router-link>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'clients.edit', params: { id: item.data.id } }" class="text-info" title="Modifier le client" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- ARCHIVE -->
                            <template v-if="!showArchived">
                                <a href="#" class="text-danger" title="Archiver le client" @click.prevent="showModal('modalArchive', item.data.id)" v-if="item.can.delete">
                                    <i class="ri-inbox-archive-fill fs-17 px-1"></i>
                                </a>
                                <i class="ri-inbox-archive-fill fs-17 px-1 text-muted" v-else></i>
                            </template>

                            <!-- RESTORE -->
                            <template v-else>
                                <a href="#" class="text-success" title="Restaurer le client" @click.prevent="restoreClient(item.data.id)" v-if="item.can.restore">
                                    <i class="ri-inbox-unarchive-fill fs-17 px-1"></i>
                                </a>
                                <i class="ri-inbox-unarchive-fill fs-17 px-1 text-muted" v-else></i>
                            </template>
                        </template>
                    </data-table>

                    <modal id="modalArchive" title="Archiver le client ?" btn-class="btn-danger" btn-text="Archiver" @click:button="archiveClient(idToArchive)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point d'archiver un client.</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
